import React, {useRef, useState} from "react";
import {Button} from "@mui/material";
import {toast} from "react-toastify";
import classNames from "classnames";
import {FaFileUpload} from "react-icons/fa";

interface UploadDocumentButtonProps {
  onFileUpload: (base64String: string, fileName: string, fileType: string) => void;
  acceptedFileTypes?: string;
  maxFileSize?: number; // in bytes
  disabled?: boolean;
  children?: React.ReactNode;
  className?: string;
}

const UploadDocumentButton = ({
                                onFileUpload,
                                acceptedFileTypes = ".pdf,.doc,.docx,.txt,.jpg,.jpeg,.png,.xlsx,.xls,.csv,.ppt,.pptx,.zip,.xml",
                                maxFileSize = 5 * 1024 * 1024, // 5MB default
                                disabled = false,
                                children = "Upload Document",
                                className,
                              }: UploadDocumentButtonProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const convertFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove the data URL prefix to get just the base64 string
        const base64String = result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file size
    if (file.size > maxFileSize) {
      toast.error(`File size exceeds ${Math.round(maxFileSize / (1024 * 1024))}MB limit`);
      return;
    }

    setIsLoading(true);

    try {
      const base64String = await convertFileToBase64(file);
      onFileUpload(base64String, file.name, file.type);
    } catch (error) {
      console.error("Error converting file to base64:", error);
      toast.error("Error uploading file. Please try again.");
    } finally {
      setIsLoading(false);
      // Reset the input value to allow uploading the same file again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className={classNames("inline-block", className)}>
      <input
        ref={fileInputRef}
        type="file"
        accept={acceptedFileTypes}
        onChange={handleFileChange}
        className="hidden"
      />
      <Button
        variant="contained"
        onClick={handleFileSelect}
        disabled={disabled || isLoading}
        startIcon={<FaFileUpload />}
      >
        {isLoading ? "Uploading..." : children}
      </Button>
    </div>
  );
};

export default UploadDocumentButton;
